import type { ResolutionStatus } from './types';

const ELIGIBLE_STATUSES = new Set<ResolutionStatus>([
  'direct_holder',
  'resolved_protocol_beneficiary',
]);

export function isEligibleStatus(status: ResolutionStatus): boolean {
  return ELIGIBLE_STATUSES.has(status);
}

const BECH32_CHARSET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';

export function isCardanoAddress(value: string): boolean {
  if (
    !value ||
    (value.toLowerCase() !== value && value.toUpperCase() !== value)
  )
    return false;
  const normalized = value.toLowerCase();
  const separator = normalized.lastIndexOf('1');
  if (separator < 1 || separator + 7 > normalized.length) return false;
  const hrp = normalized.slice(0, separator);
  if (hrp !== 'addr' && hrp !== 'stake') return false;
  const data = [...normalized.slice(separator + 1)].map((character) =>
    BECH32_CHARSET.indexOf(character)
  );
  if (data.some((item) => item < 0)) return false;
  const values = [
    ...[...hrp].map((character) => character.charCodeAt(0) >> 5),
    0,
    ...[...hrp].map((character) => character.charCodeAt(0) & 31),
    ...data,
  ];
  const generators = [
    0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3,
  ];
  let checksum = 1;
  for (const item of values) {
    const top = checksum >>> 25;
    checksum = ((checksum & 0x1ffffff) << 5) ^ item;
    for (let index = 0; index < generators.length; index += 1) {
      if ((top >>> index) & 1) checksum ^= generators[index];
    }
  }
  return checksum === 1;
}

export function formatUnitsRaw(
  raw: string,
  decimals = 6,
  maximumFractionDigits = decimals
): string {
  const negative = raw.startsWith('-');
  const digits = negative ? raw.slice(1) : raw;
  const padded = digits.padStart(decimals + 1, '0');
  const whole = decimals === 0 ? padded : padded.slice(0, -decimals);
  const fraction = decimals === 0 ? '' : padded.slice(-decimals);
  const visibleFraction = fraction
    .slice(0, maximumFractionDigits)
    .replace(/0+$/, '');
  const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${groupedWhole}${visibleFraction ? `.${visibleFraction}` : ''}`;
}

export function formatCgv(raw: string, maximumFractionDigits = 6): string {
  return formatUnitsRaw(raw, 6, maximumFractionDigits);
}

export function formatCompactCgv(raw: string): string {
  const value = Number(raw) / 1_000_000;
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatDecimalDisplay(
  value: string,
  maximumFractionDigits = 6
): string {
  const [whole = '0', fraction = ''] = value.split('.');
  const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const visibleFraction = fraction
    .slice(0, maximumFractionDigits)
    .replace(/0+$/, '');
  return visibleFraction ? `${groupedWhole}.${visibleFraction}` : groupedWhole;
}

export function formatCoverage(value: string): string {
  return `${(Number(value) * 100).toLocaleString('en-US', {
    minimumFractionDigits: Number(value) === 1 ? 0 : 2,
    maximumFractionDigits: 2,
  })}%`;
}

export function percentageRaw(partRaw: string, totalRaw: string): number {
  const total = BigInt(totalRaw);
  if (total === BigInt(0)) return 0;
  const scaled = (BigInt(partRaw) * BigInt(1_000_000)) / total;
  return Number(scaled) / 10_000;
}

export function formatPercentageRaw(partRaw: string, totalRaw: string): string {
  return `${percentageRaw(partRaw, totalRaw).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

export function shortAddress(address: string): string {
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

export function humanize(value: string): string {
  return value
    .split('_')
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(' ');
}

export function statusLabel(status: ResolutionStatus): string {
  const labels: Record<ResolutionStatus, string> = {
    direct_holder: 'Direct holder',
    resolved_protocol_beneficiary: 'Protocol attributed',
    retained_or_unresolved_contract: 'Unresolved custody',
    locked_unclaimable: 'Locked / unclaimable',
  };
  return labels[status];
}
